import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { MapPickerComponent } from '../map-picker/map-picker.component';
import { GeoLocationService } from '../../../core/services/geo-location.service';
import { GeocoderService } from '../../../core/services/geocoder.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * LocationPanelComponent provides a comprehensive location selection interface.
 * Includes coordinate inputs, GPS lookup, address search, and interactive map.
 * Implements four-way synchronization between all input methods.
 */
@Component({
  selector: 'app-location-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, MapPickerComponent, MaxLengthDirective],
  templateUrl: './location-panel.component.html',
  styleUrls: ['./location-panel.component.scss']
})
export class LocationPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  private geoLocationService = inject(GeoLocationService);
  private geocoderService = inject(GeocoderService);

  @ViewChild('addressSearchInput') addressSearchInput!: ElementRef<HTMLInputElement>;

  /** Initial latitude value (for edit mode) */
  @Input() latitude: number | null = null;

  /** Initial longitude value (for edit mode) */
  @Input() longitude: number | null = null;

  /** Initial address value (for edit mode) */
  @Input() address: string | null = null;

  /** Emitted when coordinates change via any input method */
  @Output() coordinatesChanged = new EventEmitter<{
    lat: number | null;
    lon: number | null;
    address: string | null;
    city: string | null;
    state: string | null;
  }>();

  /** Current latitude value */
  currentLat: number | null = null;

  /** Current longitude value */
  currentLon: number | null = null;

  /** Current address from reverse geocoding */
  currentAddress: string | null = null;

  /** Current city from reverse geocoding */
  currentCity: string | null = null;

  /** Current state from reverse geocoding */
  currentState: string | null = null;

  /** Loading state for GPS request */
  isLoadingLocation = false;

  /** Error message from GPS request */
  locationError: string | null = null;

  /** Subject for debouncing manual coordinate input */
  private manualInputSubject = new Subject<{ lat: number | null; lon: number | null }>();

  /** Subscriptions for cleanup */
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    // Initialize with input values
    if (this.latitude != null) {
      this.currentLat = this.latitude;
    }
    if (this.longitude != null) {
      this.currentLon = this.longitude;
    }
    if (this.address != null) {
      this.currentAddress = this.address;
    }

    // Subscribe to GeoLocation service
    this.subscriptions.push(
      this.geoLocationService.getCoordinates().subscribe((coords) => {
        if (coords.lat != null && coords.lon != null) {
          this.updateFromGPS(coords.lat, coords.lon);
        }
      })
    );

    this.subscriptions.push(
      this.geoLocationService.getIsLoading().subscribe((loading) => {
        this.isLoadingLocation = loading;
      })
    );

    this.subscriptions.push(
      this.geoLocationService.getError().subscribe((error) => {
        this.locationError = error;
      })
    );

    // Debounce manual coordinate input to avoid excessive geocoding calls
    this.subscriptions.push(
      this.manualInputSubject.pipe(debounceTime(500)).subscribe((coords) => {
        if (coords.lat != null && coords.lon != null) {
          this.reverseGeocodeAndEmit(coords.lat, coords.lon);
        }
      })
    );
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initAutocomplete();

    // If initial coordinates are set, perform reverse geocode
    if (this.currentLat != null && this.currentLon != null && !this.currentAddress) {
      this.reverseGeocodeAndEmit(this.currentLat, this.currentLon);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Initializes Google Places Autocomplete on the address search input.
   */
  private async initAutocomplete(): Promise<void> {
    const input = this.addressSearchInput?.nativeElement;
    if (!input) return;

    try {
      const [{ Autocomplete }] = await Promise.all([
        google.maps.importLibrary('places') as Promise<typeof google.maps.places>
      ]);

      const autocomplete = new Autocomplete(input, {
        types: ['geocode', 'establishment']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lon = place.geometry.location.lng();

          this.currentLat = lat;
          this.currentLon = lon;
          this.currentAddress = place.formatted_address || null;

          // Extract city and state from address components
          if (place.address_components) {
            const cityComponent = place.address_components.find(
              (c) => c.types.includes('locality') || c.types.includes('administrative_area_level_3')
            );
            const stateComponent = place.address_components.find((c) =>
              c.types.includes('administrative_area_level_1')
            );
            this.currentCity = cityComponent?.long_name || null;
            this.currentState = stateComponent?.short_name || null;
          }

          this.emitCoordinates();
        }
      });
    } catch (error) {
      console.error('Failed to initialize Places Autocomplete:', error);
    }
  }

  /**
   * Handles "Get Current Location" button click.
   */
  onGetCurrentLocation(): void {
    this.locationError = null;
    this.geoLocationService.requestLocation();
  }

  /**
   * Updates state from GPS coordinates.
   */
  private async updateFromGPS(lat: number, lon: number): Promise<void> {
    this.currentLat = lat;
    this.currentLon = lon;
    await this.reverseGeocodeAndEmit(lat, lon);
  }

  /**
   * Handles coordinate selection from map picker.
   */
  async onMapCoordinateSelected(coords: { lat: number; lon: number }): Promise<void> {
    this.currentLat = coords.lat;
    this.currentLon = coords.lon;
    await this.reverseGeocodeAndEmit(coords.lat, coords.lon);
  }

  /**
   * Handles manual latitude input change.
   */
  onLatitudeChange(value: string): void {
    const lat = value ? parseFloat(value) : null;
    if (lat !== null && (isNaN(lat) || lat < -90 || lat > 90)) {
      return;
    }
    this.currentLat = lat;
    this.manualInputSubject.next({ lat: this.currentLat, lon: this.currentLon });
  }

  /**
   * Handles manual longitude input change.
   */
  onLongitudeChange(value: string): void {
    const lon = value ? parseFloat(value) : null;
    if (lon !== null && (isNaN(lon) || lon < -180 || lon > 180)) {
      return;
    }
    this.currentLon = lon;
    this.manualInputSubject.next({ lat: this.currentLat, lon: this.currentLon });
  }

  /**
   * Performs reverse geocoding and emits the updated coordinates.
   */
  private async reverseGeocodeAndEmit(lat: number, lon: number): Promise<void> {
    const [address, city, state] = await Promise.all([
      this.geocoderService.getClosestAddress(lat, lon),
      this.geocoderService.getClosestCity(lat, lon),
      this.geocoderService.getState(lat, lon)
    ]);

    this.currentAddress = address;
    this.currentCity = city;
    this.currentState = state;

    // Update the address input field
    if (this.addressSearchInput?.nativeElement && address) {
      this.addressSearchInput.nativeElement.value = address;
    }

    this.emitCoordinates();
  }

  /**
   * Emits the current coordinate state.
   */
  private emitCoordinates(): void {
    this.coordinatesChanged.emit({
      lat: this.currentLat,
      lon: this.currentLon,
      address: this.currentAddress,
      city: this.currentCity,
      state: this.currentState
    });
  }

  /**
   * Converts decimal degrees to DMS (Degrees/Minutes/Seconds) format.
   * @param decimal The decimal degree value
   * @param isLatitude True for latitude (N/S), false for longitude (E/W)
   * @returns Formatted DMS string (e.g., "39 deg 56'14.0"N")
   */
  convertToDMS(decimal: number | null, isLatitude: boolean): string {
    if (decimal == null) {
      return '';
    }

    const absolute = Math.abs(decimal);
    const degrees = Math.floor(absolute);
    const minutesDecimal = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = ((minutesDecimal - minutes) * 60).toFixed(1);

    let direction: string;
    if (isLatitude) {
      direction = decimal >= 0 ? 'N' : 'S';
    } else {
      direction = decimal >= 0 ? 'E' : 'W';
    }

    return `${degrees} deg ${minutes}'${seconds}"${direction}`;
  }

  /**
   * Gets the DMS display string for latitude.
   */
  get latitudeDMS(): string {
    return this.convertToDMS(this.currentLat, true);
  }

  /**
   * Gets the DMS display string for longitude.
   */
  get longitudeDMS(): string {
    return this.convertToDMS(this.currentLon, false);
  }
}
